import { createContext, useContext } from "react";
import type { CurrencyCode } from "@shared/money";
import { formatMinor } from "@shared/money";

/**
 * The running total + item count for the cart being built (D-051). The Sell screen owns the cart, but the
 * total is shown in the app header's top-right (where cash on hand used to sit) and only while the Sell
 * screen is mounted. The Shell provides a reporter through this context; SellScreen publishes its summary
 * to it and the header renders it. When SellScreen renders standalone (unit tests) the reporter is null and
 * SellScreen falls back to rendering the summary inline, so the total stays observable.
 */

export interface SellSummary {
  count: number;
  totalMinor: number;
}

export const SellSummaryContext = createContext<((summary: SellSummary | null) => void) | null>(null);

/** Reporter the Shell hands SellScreen, or null when there is no Shell above it. */
export function useSellSummaryReporter(): ((summary: SellSummary | null) => void) | null {
  return useContext(SellSummaryContext);
}

export function itemCountLabel(count: number): string {
  return count === 0 ? "No items" : count === 1 ? "1 item" : `${count} items`;
}

/** The header badge: item count over the running total, right-aligned like the old cash-on-hand display. */
export function SellSummaryDisplay({ summary, currency }: { summary: SellSummary; currency: CurrencyCode }) {
  return (
    <div className="wc-sell-summary">
      <span className="wc-sell-summary__count">{itemCountLabel(summary.count)}</span>
      <span className="wc-sell-summary__total" data-testid="cart-total">
        {formatMinor(summary.totalMinor, currency)}
      </span>
    </div>
  );
}
